import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
} from 'react';
import useNavigationStats from '../hooks/dashaboard/query/useNavigationStats';

interface NavigationContextProps {
  children: ReactNode;
  initialSystemId: string; // Replace 'any' with the actual type of systemId
  organizationId: string; // Replace 'any' with the actual type of organizationId
}

interface NavigationContextValue {
  navigationData: any; // Replace 'any' with the actual type of navigation data
  updateSystemId: (newSystemId: any) => void; // Replace 'any' with the actual type of systemId
  manualUpdate: () => void;
}

const NavigationContext = createContext<NavigationContextValue | undefined>(undefined);

export const useNavigationContext = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error(
      'useNavigationContext must be used within a NavigationContextProvider'
    );
  }
  return context;
};

export const NavigationContextProvider: React.FC<NavigationContextProps> = ({
  children,
  initialSystemId,
  organizationId,
}) => {
  const [systemId, setSystemId] = useState<string | null>(initialSystemId);

  const { data, refetch: refetchNavigationData } = useNavigationStats({
    organizationId,
    systemId: systemId || initialSystemId,
  });

  const navigationData = useMemo(() => data, [data]);

  useEffect(() => {
    setSystemId(initialSystemId);
  }, [initialSystemId]);

  useEffect(() => {
    if (systemId || organizationId) {
      refetchNavigationData();
    }
  }, [systemId, organizationId, refetchNavigationData]);

  const updateSystemId = (newSystemId: any) => {
    setSystemId(newSystemId);
  };

  const manualUpdate = () => {
    refetchNavigationData();
  };

  const contextValue: NavigationContextValue = {
    navigationData,
    updateSystemId,
    manualUpdate,
  };

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
};
